import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Form,
  InputGroup,
  Dropdown,
  OverlayTrigger,
  Tooltip,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { 
  User, 
  UserStats, 
  UserFilters, 
  UserRole, 
  UserStatus, 
  getUserStatusBadgeVariant, 
  getUserRoleBadgeVariant,
  formatUserType,
  formatPremiumStatus,
  isUserPremium,
} from '../../Data/Users';
import userService from '../../../components/User/services/userService';
import { showSuccess, showError, showDeleteConfirm, showConfirm } from '../../../Utilities/sweetAlert';
import { COLORS } from '../../constants/colors';
import './UserManagement.css';

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    pageSize: 20
  });
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  // Fetch users with current filters
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userService.getEnhancedUsers(filters);
      
      // The backend returns { data: { users: { users: [...], totalCount: ... } } }
      const usersWrapper = response.data.users;
      const usersArray = Array.isArray(usersWrapper?.users) ? usersWrapper.users : [];
      
      setUsers(usersArray);
      setTotalCount(usersWrapper?.totalCount || 0);
      setTotalPages(usersWrapper?.totalPages || 0);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
      showError('Failed to fetch users');
      setUsers([]); // Ensure users is always an array
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch user statistics
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await userService.getUserStats(selectedPeriod);
      setStats(response.data.stats);
    } catch (err: any) {
      console.error('Failed to fetch user statistics:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Handle filter changes
  const handleFilterChange = (key: keyof UserFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset to page 1 when other filters change
    }));
  };

  // Handle search
  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      name: searchTerm,
      page: 1
    }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    handleFilterChange('page', page);
  };

  // Handle user status update
  const handleStatusUpdate = async (userId: number, newStatus: UserStatus) => {
    try {
      await userService.updateUserStatus(userId, newStatus);
      showSuccess(`User status updated to ${newStatus}`);
      fetchUsers();
    } catch (err: any) {
      showError('Failed to update user status');
    }
  };

  // Handle user role update
  const handleRoleUpdate = async (userId: number, newRole: UserRole) => {
    try {
      await userService.updateUserRole(userId, newRole);
      showSuccess(`User role updated to ${newRole}`);
      fetchUsers();
    } catch (err: any) {
      showError('Failed to update user role');
    }
  };

  // Handle revoke premium
  const handleRevokePremium = async (userId: number, userName: string) => {
    if (!window.confirm(`Revoke premium for ${userName}? This will immediately remove premium access.`)) {
      return;
    }
    try {
      await userService.revokePremium(userId);
      showSuccess(`Premium revoked for ${userName}`);
      fetchUsers();
    } catch (err) {
      console.error('Failed to revoke premium:', err);
      showError('Failed to revoke premium');
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (userId: number, userName: string) => {
    const confirmed = await showDeleteConfirm(
      `Are you sure you want to permanently delete user "${userName}"?`,
      'This action cannot be undone!'
    );
    
    if (confirmed) {
      try {
        await userService.deleteUser(userId);
        showSuccess(`User ${userName} has been deleted`);
        fetchUsers();
        fetchStats(); // Refresh stats after deletion
      } catch (err: any) {
        showError('Failed to delete user');
      }
    }
  };

  // View user details
  const handleViewUser = (userId: number) => {
    navigate(`/users/${userId}`);
  };

  // Generate pagination buttons
  const renderPagination = () => {
    const buttons = [];
    const currentPage = filters.page || 1;
    
    // Previous button
    buttons.push(
      <Button
        key="prev"
        variant="outline-primary"
        size="sm"
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
        className="me-1"
      >
        Previous
      </Button>
    );

    // Page numbers (show max 5 pages around current)
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let page = startPage; page <= endPage; page++) {
      buttons.push(
        <Button
          key={page}
          variant={page === currentPage ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => handlePageChange(page)}
          className="me-1"
        >
          {page}
        </Button>
      );
    }

    // Next button
    buttons.push(
      <Button
        key="next"
        variant="outline-primary"
        size="sm"
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        Next
      </Button>
    );

    return buttons;
  };

  if (loading && users.length === 0) {
    return (
      <Container fluid className="user-management-container">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="user-management-container">
      <Row className="mb-4">
        <Col>
          <h2 className="page-title">User Management</h2>
          <p className="text-muted">Manage users, view statistics, and control access</p>
        </Col>
      </Row>

      {/* Statistics Dashboard */}
      <Row className="mb-4">
        <Col>
          <Card className="stats-card">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">User Statistics</h5>
              <Form.Select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                style={{ width: 'auto' }}
                size="sm"
              >
                <option value="all">All Time</option>
                <option value="year">This Year</option>
                <option value="month">This Month</option>
                <option value="week">This Week</option>
                <option value="today">Today</option>
              </Form.Select>
            </Card.Header>
            <Card.Body>
              {statsLoading ? (
                <div className="text-center">
                  <Spinner size="sm" /> Loading statistics...
                </div>
              ) : stats ? (
                <Row>
                  <Col md={2} className="mb-3">
                    <div className="stat-item">
                      <div className="stat-number" style={{ color: COLORS.PRIMARY_ORANGE }}>{stats.totalUsers}</div>
                      <div className="stat-label">Total Users</div>
                    </div>
                  </Col>
                  <Col md={2} className="mb-3">
                    <div className="stat-item">
                      <div className="stat-number" style={{ color: COLORS.BUTTON_GREEN }}>{stats.signedUpUsers}</div>
                      <div className="stat-label">Registered</div>
                    </div>
                  </Col>
                  <Col md={2} className="mb-3">
                    <div className="stat-item">
                      <div className="stat-number" style={{ color: COLORS.MEDIUM_GREY }}>{stats.guestUsers}</div>
                      <div className="stat-label">Guests</div>
                    </div>
                  </Col>
                  <Col md={2} className="mb-3">
                    <div className="stat-item">
                      <div className="stat-number" style={{ color: COLORS.PRIMARY_ORANGE }}>{stats.premiumUsers}</div>
                      <div className="stat-label">Premium</div>
                    </div>
                  </Col>
                  <Col md={2} className="mb-3">
                    <div className="stat-item">
                      <div className="stat-number" style={{ color: COLORS.SECONDARY_GREEN }}>{stats.usersByStatus.active}</div>
                      <div className="stat-label">Active</div>
                    </div>
                  </Col>
                  <Col md={2} className="mb-3">
                    <div className="stat-item">
                      <div className="stat-number" style={{ color: COLORS.LIGHT_GREY }}>{stats.recentRegistrations}</div>
                      <div className="stat-label">New (7 days)</div>
                    </div>
                  </Col>
                </Row>
              ) : (
                <Alert variant="warning">Failed to load statistics</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Row className="mb-3">
        <Col md={4}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button variant="outline-secondary" onClick={handleSearch}>
              Search
            </Button>
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
          >
            <option value="">All Statuses</option>
            {Object.values(UserStatus).map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            value={filters.role || ''}
            onChange={(e) => handleFilterChange('role', e.target.value || undefined)}
          >
            <option value="">All Roles</option>
            {Object.values(UserRole).map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            value={filters.userType || ''}
            onChange={(e) => handleFilterChange('userType', e.target.value || undefined)}
          >
            <option value="">All Types</option>
            <option value="signedUp">Registered</option>
            <option value="guest">Guest</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            value={filters.isPremium || ''}
            onChange={(e) => handleFilterChange('isPremium', e.target.value || undefined)}
          >
            <option value="">All Premium Status</option>
            <option value="true">Premium</option>
            <option value="false">Standard</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Users Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Users ({totalCount})</h5>
              <div className="d-flex align-items-center">
                <span className="me-3 text-muted">
                  Page {filters.page} of {totalPages}
                </span>
                <Form.Select
                  value={filters.pageSize}
                  onChange={(e) => handleFilterChange('pageSize', parseInt(e.target.value))}
                  style={{ width: 'auto' }}
                  size="sm"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </Form.Select>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {error && (
                <Alert variant="danger" className="m-3 mb-0">
                  {error}
                </Alert>
              )}
              
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>User</th>
                      <th>Contact</th>
                      <th>Status</th>
                      <th>Role</th>
                      <th>Type</th>
                      <th>Premium</th>
                      <th>Activity</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="text-center py-4">
                          <Spinner size="sm" /> Loading users...
                        </td>
                      </tr>
                    ) : !users || !Array.isArray(users) || users.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-4 text-muted">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              {user.photo && (
                                <img
                                  src={user.photo}
                                  alt={user.name}
                                  className="user-avatar me-2"
                                />
                              )}
                              <div>
                                <div className="fw-semibold">{user.name}</div>
                                <small className="text-muted">@{user.username}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{user.email}</Tooltip>}
                              >
                                <div className="text-truncate" style={{ maxWidth: '150px' }}>
                                  {user.email}
                                </div>
                              </OverlayTrigger>
                              {user.phoneNumber && (
                                <small className="text-muted d-block">
                                  {user.phoneNumber}
                                </small>
                              )}
                            </div>
                          </td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle
                                as={Badge}
                                bg={getUserStatusBadgeVariant(user.status)}
                                className="user-status-badge"
                              >
                                {user.status}
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                {Object.values(UserStatus).map(status => (
                                  <Dropdown.Item
                                    key={status}
                                    onClick={() => handleStatusUpdate(user.id, status)}
                                    active={user.status === status}
                                  >
                                    {status}
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle
                                as={Badge}
                                bg={getUserRoleBadgeVariant(user.role)}
                                className="user-role-badge"
                              >
                                {user.role}
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                {Object.values(UserRole).map(role => (
                                  <Dropdown.Item
                                    key={role}
                                    onClick={() => handleRoleUpdate(user.id, role)}
                                    active={user.role === role}
                                  >
                                    {role}
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                          <td>
                            <Badge bg={user.email.includes('guest') ? 'secondary' : 'info'}>
                              {formatUserType(user.email)}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={isUserPremium(user) ? 'warning' : 'light'} text="dark">
                              {formatPremiumStatus(user)}
                            </Badge>
                          </td>
                          <td>
                            <div>
                              <small className="text-muted d-block">
                                {user.hikeCount || 0} hikes
                              </small>
                              <small className="text-muted">
                                {user.savedTrails?.length || 0} saved
                              </small>
                            </div>
                          </td>
                          <td>
                            <div className="user-actions">
                              {isUserPremium(user) && (
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleRevokePremium(user.id, user.name)}
                                  className="me-1"
                                >
                                  Revoke Premium
                                </Button>
                              )}
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewUser(user.id)}
                                className="me-1"
                              >
                                View
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteUser(user.id, user.name)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <Card.Footer className="d-flex justify-content-center">
                <div className="pagination-container">
                  {renderPagination()}
                </div>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

    </Container>
  );
};

export default UserManagement;